import React, { useState, useEffect } from 'react';
import emailjs from '@emailjs/browser';
import './Support.css';

export default function Support() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [subjectLocked, setSubjectLocked] = useState(false);
  const [hiddenMetadata, setHiddenMetadata] = useState<string | null>(null);
  const [fromApp, setFromApp] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [fromPage, setFromPage] = useState<string | null>(null);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);

    const preselectedSubject = urlParams.get('subject');
    const metadata = urlParams.get('metadata');
    const lockSubject = urlParams.get('lock_subject') === 'true';
    const isFromApp = urlParams.get('from') === 'app';
    const urlToken = urlParams.get('token');
    const pageFrom = urlParams.get('from');

    setFormData(prev => ({
      ...prev,
      subject: preselectedSubject || ''
    }));

    if (metadata) {
      setHiddenMetadata(metadata);
    }

    setSubjectLocked(lockSubject);
    setFromApp(isFromApp);
    setToken(urlToken);
    setFromPage(pageFrom);

    if (metadata) {
      urlParams.delete('metadata');
      const cleanUrl = urlParams.toString()
        ? `${window.location.pathname}?${urlParams.toString()}`
        : window.location.pathname;

      window.history.replaceState({}, '', cleanUrl);
    }
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Send email via EmailJS
      await emailjs.send(
        'service_x4p336c',
        'template_vbskywf',
        {
          from_name: formData.name,
          from_email: formData.email,
          subject: formData.subject,
          message: formData.message,
          metadata: hiddenMetadata ? decodeURIComponent(hiddenMetadata) : '',
          to_email: 'prawahp@gmail.com'
        },
        'fcFNzayxcbRrS8qiZ'
      );

      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
      setSubmitted(true);
      setSubjectLocked(false);
      setHiddenMetadata(null);

      setTimeout(() => {
        setSubmitted(false);
      }, 5000);
    } catch (error) {
      alert('Failed to send email. Please try again or contact prasiddha@gmail.com directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = formData.name && formData.email && formData.subject && formData.message;

  return (
    <div className="support-container">
      <div className="support-content">
        {!fromApp && (
          <button
            onClick={() => {
              if (fromPage === 'privacy') {
                window.location.href = token ? `/privacy?token=${token}` : '/privacy';
              } else if (fromPage === 'terms') {
                window.location.href = token ? `/terms?token=${token}` : '/terms';
              } else {
                window.location.href = token ? `/?token=${token}` : '/';
              }
            }}
            style={{
              background: 'none',
              border: '1px solid #ddd',
              color: '#666',
              padding: '0.5rem 1rem',
              borderRadius: '8px',
              cursor: 'pointer',
              marginBottom: '2rem',
              fontSize: '0.9rem'
            }}
          >
            ← Back
          </button>
        )}

        <div className={`support-header${fromApp ? ' from-app' : ''}`}>
          <h1>Get Support</h1>
          <p className="support-description">
            Need help with PhomoCam? We're here to assist you. Fill out the form below and we'll get back to you as soon as possible.
          </p>
        </div>

        {submitted && (
          <div className="success-message">
            <div className="success-icon">✓</div>
            <h3>Email sent successfully!</h3>
            <p>Your support request has been sent directly to our team. We'll get back to you within 24-48 hours at <strong>{formData.email}</strong>.</p>
          </div>
        )}

        <form className="support-form" onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="name">Full Name *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Enter your full name"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="email">Email Address *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="Enter your email address"
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="subject">Subject *</label>
            <select
              id="subject"
              name="subject"
              value={formData.subject}
              onChange={handleInputChange}
              disabled={subjectLocked}
              required
              style={subjectLocked ? { cursor: 'not-allowed' } : {}}
            >
              <option value="">Select a topic</option>
              <option value="Account Issues">Account Issues</option>
              <option value="Face Recognition Problems">Face Recognition Problems</option>
              <option value="Photo Sharing Issues">Photo Sharing Issues</option>
              <option value="App Crashes or Bugs">App Crashes or Bugs</option>
              <option value="Privacy/Terms Concerns">Privacy/Terms Concerns</option>
              <option value="Feature Request">Feature Request</option>
              <option value="Billing Questions">Billing Questions</option>
              <option value="Data Deletion Request">Data Deletion Request</option>
              <option value="Report Inappropriate Photo">Report Inappropriate Photo</option>
              <option value="Report User Behavior">Report User Behavior</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="message">Message *</label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleInputChange}
              placeholder="Please describe your issue or question in detail..."
              rows={8}
              required
            />
          </div>

          <div className="form-actions">
            <button 
              type="submit" 
              className="submit-button"
              disabled={!isFormValid || isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <div className="spinner"></div>
                  Sending email...
                </>
              ) : (
                'Send Support Email'
              )}
            </button>
          </div>
        </form>

        <div className="support-info">
          <div className="info-section">
            <h3>📋 Before You Contact Us</h3>
            <ul>
              <li>Check your app version (latest version recommended)</li>
              <li>Include details about your device and OS version</li>
              <li>Describe the steps that led to the issue</li>
              <li>Mention if the problem is reproducible</li>
            </ul>
          </div>

          <div className="info-section">
            <h3>⏰ Response Time</h3>
            <p>We typically respond within 24-48 hours. For urgent privacy or security concerns, we prioritize faster responses.</p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="support-footer">
        © 2025 PhomoCam. Share memories, not just photos.
      </div>
    </div>
  );
}